import { keepPreviousData } from '@tanstack/react-query';
import {
    type ColumnDef,
    type PaginationState,
    flexRender,
    getCoreRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { MoreHorizontal, Plus, Users } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import type { ListAdminInstructors } from '@workspace/client';
import {
    Avatar,
    AvatarFallback,
    AvatarImage,
    Badge,
    Button,
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuTrigger,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
} from '@workspace/shadcn';
import { ConfirmDialog, SearchInput, useAction, useData } from '@workspace/ui';

import { CountablePageHeader } from '../../components/page-header.tsx';
import { Pagination } from '../../components/pagination.tsx';
import {
    StickyTableBox,
    StickyTableHeader,
} from '../../components/sticky-table-box.tsx';
import { InstructorDialog } from './instructor-dialog.tsx';

type Instructor = ListAdminInstructors['records'][number];

function initials(name: string) {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

export default function InstructorsPage() {
  const [searchQuery, setSearchQuery] = useState<string | undefined>('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Instructor | null>(null);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const deleteMutation = useAction('DELETE /admin/instructors/{id}', {
    invalidate: ['GET /admin/instructors'],
    meta: { toast: true },
    onSuccess: () => {
      setDeleteTarget(null);
      toast.success('Instructor deleted');
    },
  });

  const columns: ColumnDef<Instructor>[] = [
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => (
        <div className="flex items-center gap-3">
          <Avatar className="size-8">
            <AvatarImage src={row.original.imageUrl} alt={row.original.name} />
            <AvatarFallback>{initials(row.original.name)}</AvatarFallback>
          </Avatar>
          <div className="font-medium">{row.original.name}</div>
        </div>
      ),
    },
    {
      accessorKey: 'position',
      header: 'Position',
      cell: ({ row }) => (
        <div className="text-muted-foreground">{row.original.position}</div>
      ),
    },
    {
      accessorKey: 'institution',
      header: 'Institution',
      cell: ({ row }) => (
        <div className="text-muted-foreground">{row.original.institution}</div>
      ),
    },
    {
      id: 'courseCount',
      header: 'Courses',
      cell: ({ row }) => (
        <Badge variant="outline">{row.original.courseCount}</Badge>
      ),
    },
    {
      id: 'actions',
      cell: ({ row }) => {
        const it = row.original;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Open menu</span>
                <MoreHorizontal className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Actions</DropdownMenuLabel>
              <InstructorDialog
                instructor={it}
                trigger={
                  <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                    Edit instructor
                  </DropdownMenuItem>
                }
              />
              <DropdownMenuItem
                className="text-destructive"
                onSelect={(e) => {
                  e.preventDefault();
                  setDeleteTarget(it);
                }}
              >
                Delete instructor
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  const dataQuery = useData(
    'GET /admin/instructors',
    {
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      search: searchQuery || undefined,
    },
    { placeholderData: keepPreviousData },
  );

  const table = useReactTable({
    data: dataQuery.data?.records || [],
    columns,
    manualPagination: true,
    rowCount: dataQuery.data?.totalCount,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    state: { pagination },
  });

  return (
    <div className="flex min-h-0 w-full flex-1 flex-col">
      <CountablePageHeader
        title="Instructors"
        totalCount={`${dataQuery.data?.totalCount || 0} total instructors`}
        icon={Users}
      >
        <InstructorDialog
          open={createDialogOpen}
          onOpenChange={setCreateDialogOpen}
          trigger={
            <Button>
              <Plus className="mr-2 size-4" />
              Add Instructor
            </Button>
          }
        />
      </CountablePageHeader>

      <div className="mb-4">
        <SearchInput
          value={searchQuery}
          onChange={setSearchQuery}
          onClear={() => setSearchQuery('')}
        />
      </div>

      <StickyTableBox>
        <Table>
          <StickyTableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </StickyTableHeader>
          <TableBody>
            {dataQuery.isLoading ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  Loading...
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </StickyTableBox>

      <Pagination
        currentPage={table.getState().pagination.pageIndex}
        pageSize={table.getState().pagination.pageSize}
        pageCount={table.getPageCount()}
        totalCount={table.getRowCount()}
        onPageChange={table.setPageIndex}
        onPageSizeChange={table.setPageSize}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        mutation={{
          mutate: () => {
            if (deleteTarget) {
              deleteMutation.mutate({ id: deleteTarget.id });
            }
          },
          isPending: deleteMutation.isPending,
        }}
        title="Delete instructor"
        description={
          deleteTarget ? (
            <span>
              Are you sure you want to delete{' '}
              <strong>{deleteTarget.name}</strong>? This cannot be undone.
              {deleteTarget.courseCount > 0 && (
                <>
                  {' '}
                  They are still listed on {deleteTarget.courseCount} course(s)
                  and must be detached first.
                </>
              )}
            </span>
          ) : (
            ''
          )
        }
      />
    </div>
  );
}
